import React, { useMemo, useRef, useEffect } from 'react';
import * as diff from 'diff';
import { extractPlainTextFromTipTap } from '../utils/tipTapUtils';
import './DiffView.css';

interface DiffViewProps {
    originalContent: Record<string, any> | null | undefined;
    reviewedContent: Record<string, any> | null | undefined;
    viewMode?: 'side-by-side' | 'unified';
    scrollToHeading?: string; // Heading text to scroll to in reviewed side
    searchQuery?: string; // Search query to highlight in the diff view
}

const DiffView: React.FC<DiffViewProps> = ({
    originalContent,
    reviewedContent,
    viewMode = 'side-by-side',
    scrollToHeading,
    searchQuery
}) => {
    const leftPanelRef = useRef<HTMLDivElement>(null);
    const rightPanelRef = useRef<HTMLDivElement>(null);
    const isScrollingRef = useRef(false);

    const diffResult = useMemo(() => {
        const originalText = extractPlainTextFromTipTap(originalContent) || '';
        const reviewedText = extractPlainTextFromTipTap(reviewedContent) || '';
        
        // Debug: Log if texts are the same (only in development)
        if (process.env.NODE_ENV === 'development' && originalText === reviewedText && originalText.length > 0) {
            console.log('[DiffView] Original and reviewed texts are identical');
            console.log('[DiffView] Original content:', originalContent);
            console.log('[DiffView] Reviewed content:', reviewedContent);
        }
        
        // Use word-level diff for better granularity with prose content
        const wordDiff = diff.diffWords(originalText, reviewedText);
        
        // Also compute line-level diff for side-by-side view
        const lineDiff = diff.diffLines(originalText, reviewedText);
        
        return {
            wordDiff,
            lineDiff
        };
    }, [originalContent, reviewedContent]);

    // Helper function to highlight search terms in text
    const highlightSearchTerms = (text: string, query?: string): React.ReactNode => {
        if (!query || !query.trim() || !text) return text;
        
        const trimmedQuery = query.trim();
        const queryLower = trimmedQuery.toLowerCase();
        const textLower = text.toLowerCase();
        const parts: React.ReactNode[] = [];
        let lastIndex = 0;
        let index = textLower.indexOf(queryLower, lastIndex);
        
        while (index !== -1) {
            // Add text before match
            if (index > lastIndex) {
                parts.push(text.substring(lastIndex, index));
            }
            
            // Add highlighted match - use trimmed query length
            parts.push(
                <mark key={index} className="diff-search-highlight">
                    {text.substring(index, index + trimmedQuery.length)}
                </mark>
            );
            
            lastIndex = index + trimmedQuery.length;
            index = textLower.indexOf(queryLower, lastIndex);
        }
        
        // Add remaining text
        if (lastIndex < text.length) {
            parts.push(text.substring(lastIndex));
        }
        
        return parts.length > 0 ? <>{parts}</> : text;
    };

    const renderSideBySide = () => {
        const { lineDiff } = diffResult;
        
        // Build line-by-line comparison
        const leftLines: Array<{ text: string; type: 'removed' | 'unchanged' | 'empty' }> = [];
        const rightLines: Array<{ text: string; type: 'added' | 'unchanged' | 'empty' }> = [];
        
        lineDiff.forEach((part) => {
            const lines = part.value.split('\n');
            // Remove empty string from split if it's the last element
            if (lines[lines.length - 1] === '') {
                lines.pop();
            }
            
            if (part.added) {
                // Added lines go to right side only
                lines.forEach((line) => {
                    rightLines.push({ text: line, type: 'added' });
                    leftLines.push({ text: '', type: 'empty' });
                });
            } else if (part.removed) {
                // Removed lines go to left side only
                lines.forEach((line) => {
                    leftLines.push({ text: line, type: 'removed' });
                    rightLines.push({ text: '', type: 'empty' });
                });
            } else {
                // Unchanged lines go to both sides
                lines.forEach((line) => {
                    leftLines.push({ text: line, type: 'unchanged' });
                    rightLines.push({ text: line, type: 'unchanged' });
                });
            }
        });
        
        return (
            <div className="diff-view-side-by-side">
                <div className="diff-view-panel diff-view-original">
                    <div className="diff-view-header">Original</div>
                    <div 
                        ref={leftPanelRef}
                        className="diff-view-content"
                        data-panel="left"
                    >
                        {leftLines.map((line, index) => (
                            <div
                                key={index}
                                className={`diff-view-line diff-line-${line.type}`}
                                data-line-number={index + 1}
                            >
                                {line.type === 'removed' || line.type === 'unchanged' ? (
                                    <span>{searchQuery ? highlightSearchTerms(line.text, searchQuery) : line.text}</span>
                                ) : (
                                    <span className="diff-line-number">{index + 1}</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="diff-view-panel diff-view-reviewed">
                    <div className="diff-view-header">Reviewed</div>
                    <div 
                        ref={rightPanelRef}
                        className="diff-view-content"
                        data-panel="right"
                    >
                        {rightLines.map((line, index) => (
                            <div
                                key={index}
                                className={`diff-view-line diff-line-${line.type}`}
                                data-line-number={index + 1}
                            >
                                {line.type === 'added' || line.type === 'unchanged' ? (
                                    <span>{searchQuery ? highlightSearchTerms(line.text, searchQuery) : line.text}</span>
                                ) : (
                                    <span className="diff-line-number">{index + 1}</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const renderUnified = () => {
        const { wordDiff } = diffResult;
        
        return (
            <div className="diff-view-unified">
                <div className="diff-view-header">Unified Diff</div>
                <div className="diff-view-content">
                    {wordDiff.map((part, index) => {
                        const className = part.added 
                            ? 'diff-added' 
                            : part.removed 
                            ? 'diff-removed' 
                            : 'diff-unchanged';
                        
                        return (
                            <span key={index} className={className}>
                                {searchQuery ? highlightSearchTerms(part.value, searchQuery) : part.value}
                            </span>
                        );
                    })}
                </div>
            </div>
        );
    };

    // Scroll to heading in reviewed side when scrollToHeading prop changes
    useEffect(() => {
        if (!scrollToHeading || viewMode !== 'side-by-side' || !rightPanelRef.current) {
            return;
        }

        const rightPanel = rightPanelRef.current;
        const headingTextLower = scrollToHeading.trim().toLowerCase();
        
        // Find the line that contains this heading text
        const lineElements = rightPanel.querySelectorAll('[data-line-number]');
        let targetLine: HTMLElement | null = null;
        
        lineElements.forEach((lineEl) => {
            const element = lineEl as HTMLElement;
            if (!element) return;
            
            const lineText = element.textContent?.trim().toLowerCase() || '';
            
            // Check if this line contains the heading text (exact match or starts with)
            if (lineText === headingTextLower || lineText.startsWith(headingTextLower)) {
                // Prefer exact match, but take first match if no exact match found
                if (!targetLine || lineText === headingTextLower) {
                    targetLine = element;
                }
            }
        });
        
        if (targetLine) {
            // Scroll to the target line with smooth behavior
            const lineElement = targetLine as HTMLElement;
            lineElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            
            // Highlight the line briefly
            const originalBg = lineElement.style.backgroundColor || '';
            lineElement.style.backgroundColor = '#e8f0fe';
            setTimeout(() => {
                lineElement.style.backgroundColor = originalBg;
            }, 2000);
        }
    }, [scrollToHeading, viewMode]);

    // Synchronize scrolling between panels
    useEffect(() => {
        if (viewMode !== 'side-by-side' || !leftPanelRef.current || !rightPanelRef.current) {
            return;
        }

        const leftPanel = leftPanelRef.current;
        const rightPanel = rightPanelRef.current;

        // Calculate which line is at the top of the viewport
        const getTopLineNumber = (panel: HTMLDivElement): number => {
            const scrollTop = panel.scrollTop;
            const padding = 8; // padding from CSS
            const viewportTop = scrollTop + padding;
            
            // Find the line element that's closest to the top of the viewport
            const lineElements = panel.querySelectorAll('[data-line-number]');
            let closestLine = 1;
            let minDistance = Infinity;
            
            lineElements.forEach((lineEl) => {
                const element = lineEl as HTMLElement;
                const elementTop = element.offsetTop;
                const distance = Math.abs(elementTop - viewportTop);
                
                if (distance < minDistance) {
                    minDistance = distance;
                    const lineNumber = parseInt(element.getAttribute('data-line-number') || '1');
                    closestLine = lineNumber;
                }
            });
            
            return closestLine;
        };

        // Scroll to a specific line number
        const scrollToLine = (panel: HTMLDivElement, lineNumber: number) => {
            const lineElement = panel.querySelector(`[data-line-number="${lineNumber}"]`) as HTMLElement;
            if (lineElement) {
                const padding = 8; // padding from CSS
                const targetScrollTop = lineElement.offsetTop - padding;
                panel.scrollTop = Math.max(0, targetScrollTop);
            }
        };

        const handleLeftScroll = () => {
            if (isScrollingRef.current) return;
            isScrollingRef.current = true;
            const lineNumber = getTopLineNumber(leftPanel);
            scrollToLine(rightPanel, lineNumber);
            requestAnimationFrame(() => {
                isScrollingRef.current = false;
            });
        };

        const handleRightScroll = () => {
            if (isScrollingRef.current) return;
            isScrollingRef.current = true;
            const lineNumber = getTopLineNumber(rightPanel);
            scrollToLine(leftPanel, lineNumber);
            requestAnimationFrame(() => {
                isScrollingRef.current = false;
            });
        };

        leftPanel.addEventListener('scroll', handleLeftScroll);
        rightPanel.addEventListener('scroll', handleRightScroll);

        return () => {
            leftPanel.removeEventListener('scroll', handleLeftScroll);
            rightPanel.removeEventListener('scroll', handleRightScroll);
        };
    }, [viewMode, diffResult]);

    if (!originalContent && !reviewedContent) {
        return (
            <div className="diff-view-empty">
                <p>No content to compare</p>
            </div>
        );
    }

    return (
        <div className="diff-view-container">
            {viewMode === 'side-by-side' ? renderSideBySide() : renderUnified()}
        </div>
    );
};

export default DiffView;

